import { API_BASE_URL } from '@/app/config/apiConfig';
import type {
  AuthApiError,
  LoginRequest,
  LoginResponse,
  PhoneRegistrationResponse,
  RefreshTokenResponse,
  SendCodeRequest,
  VerifyTokenResponse,
} from '@/types/auth';

class AuthApiErrorImpl extends Error implements AuthApiError {
  code: number;

  constructor(code: number, message: string) {
    super(message);
    this.name = 'AuthApiError';
    this.code = code;
  }
}

/**
 * wx-ai-user 服务的标准响应格式
 * 后端 auth_proxy_router 透传 wx-ai-user 的响应，格式为 { status, data, message }
 */
interface WxAiUserResponse<T> {
  status: number;
  data: T;
  message: string;
}

interface VerifyTokenOptions {
  awardDailyAccess?: boolean;
}

const REGISTRATION_DOMAIN_HEADER = 'X-Registration-Domain';

function getRegistrationDomainHeaders(): HeadersInit {
  if (typeof window === 'undefined') {
    return {};
  }
  const hostname = window.location.hostname.trim();
  return hostname ? { [REGISTRATION_DOMAIN_HEADER]: hostname } : {};
}

async function authFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const url = `${API_BASE_URL}${path}`;

  const defaultHeaders: HeadersInit = {
    'Content-Type': 'application/json',
  };

  const config: RequestInit = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options?.headers,
    },
  };

  const response = await fetch(url, config);

  if (!response.ok) {
    let errorCode = 0;
    let errorMessage = '请求失败';
    try {
      const errorData = await response.json();
      errorCode = typeof errorData.code === 'number' ? errorData.code : 0;
      errorMessage = typeof errorData.message === 'string' ? errorData.message : '请求失败';
    } catch {
      errorMessage = response.statusText || '请求失败';
    }
    throw new AuthApiErrorImpl(errorCode, errorMessage);
  }

  const body: WxAiUserResponse<T> = await response.json();

  // wx-ai-user 业务层错误（HTTP 200 但 status 非 200）
  if (body.status !== 200) {
    throw new AuthApiErrorImpl(body.status, body.message || '请求失败');
  }

  return body.data;
}

export const AuthApi = {
  async sendCode(phone: string, invitationCode?: string): Promise<void> {
    const payload: SendCodeRequest = { phone };
    if (invitationCode) {
      payload.invitation_code = invitationCode;
    }

    await authFetch<void>('/auth/send-code', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  async checkPhoneRegistered(phone: string): Promise<PhoneRegistrationResponse> {
    return authFetch<PhoneRegistrationResponse>('/auth/check-phone', {
      method: 'POST',
      body: JSON.stringify({ phone }),
    });
  },

  async login(
    phone: string,
    code: string,
    deviceId: string,
    invitationCode?: string,
  ): Promise<LoginResponse> {
    const payload: LoginRequest = {
      phone,
      code,
      device_id: deviceId,
    };
    if (invitationCode) {
      payload.invitation_code = invitationCode;
    }

    return authFetch<LoginResponse>('/auth/login', {
      method: 'POST',
      headers: getRegistrationDomainHeaders(),
      body: JSON.stringify(payload),
    });
  },

  async refreshToken(token: string, uid: number): Promise<RefreshTokenResponse> {
    return authFetch<RefreshTokenResponse>('/auth/refresh', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'X-User-Uid': String(uid),
      },
      body: JSON.stringify({ uid }),
    });
  },

  async logout(token: string, uid: number): Promise<void> {
    await authFetch<void>('/auth/logout', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'X-User-Uid': String(uid),
      },
      body: JSON.stringify({ token, uid }),
    });
  },

  async verifyToken(
    token: string,
    uid: number,
    options: VerifyTokenOptions = {},
  ): Promise<VerifyTokenResponse> {
    const { awardDailyAccess = false } = options;
    return authFetch<VerifyTokenResponse>('/auth/verify', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ uid, award_daily_access: awardDailyAccess }),
    });
  },
};
